import React, { useState, useEffect, useMemo } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { useTimeFrame } from '../../contexts/TimeFrameContext';
import { formatMonthName, getYearFromMonth } from '../../utils/formatters';
import { getHeatmapColor as getHeatmapColorHelper } from '../../utils/themeHelpers';
import { ChartInfoButton } from '../ui';
import useChartGuide from '../../hooks/useChartGuide';
import { CHART_GUIDES } from '../../constants/chartGuides';
import { useEvaluationHeatmapData } from '../../hooks/useEvaluationQueries';
import { EvaluationMetrics } from '../../types/evaluation';

interface HeatMapProps {
  branchId?: number;
  timeRange?: number;
  onError?: (error: Error) => void;
  showLoading?: boolean;
  compact?: boolean;
  selectedMetric: string;
  comparisonSource: 'brand' | 'all' | 'average12';
  onMonthSelect?: (month: string, data: EvaluationMetrics | null) => void;
  metrics: Array<{ id: string; name: string; unit: string; type: string }>;
  average12MonthData?: Record<string, number>;
}

const HeatMap: React.FC<HeatMapProps> = ({
  branchId = 0,
  timeRange,
  onError,
  showLoading = true,
  compact = false,
  selectedMetric,
  comparisonSource,
  onMonthSelect,
  metrics,
  average12MonthData
}) => {
  const { currentTheme } = useTheme();
  const { timeFrame } = useTimeFrame();
  const { openGuide } = useChartGuide();
  
  const [selectedMonth, setSelectedMonth] = useState<string | null>(null);

  // Convert timeFrame from context to number of months
  const getTimeframeFromContext = useMemo(() => {
    if (timeRange) return timeRange;
    switch (timeFrame) {
      case 'last1Month': return 1;
      case 'last3Months': return 3;
      case 'last6Months': return 6;
      case 'lastYear': return 12;
      default: return 6;
    }
  }, [timeFrame, timeRange]);

  // Use React Query hook for data fetching
  const { 
    branchData = [], 
    brandData = [], 
    allRestaurantsData = [], 
    allMonths = [],
    dataByMonth,
    isLoading, 
    isError, 
    error 
  } = useEvaluationHeatmapData(branchId, getTimeframeFromContext);

  // Handle errors
  useEffect(() => {
    if (isError && onError && error) {
      onError(error as Error);
    }
  }, [isError, error, onError]);

  // Prepare heatmap data
  const diffData = useMemo(() => {
    if (!allMonths || allMonths.length === 0) {
      return [];
    }
    
    return allMonths.map((month) => {
      let monthData = dataByMonth.byMonth[month];
    
      if (!monthData && typeof month === 'string' && month.includes('-')) {
        const monthNameOnly = month.split('-')[0];
        monthData = dataByMonth.byMonthName[monthNameOnly];
      }  
      
      const diffObj: any = { 
        month,
        actualMonth: month
      };
      
      if (monthData) {
        const item = monthData.data;
        diffObj.actualMonth = item.month;
        
        let avgData = null;
        
        if (comparisonSource === 'average12' && average12MonthData) {
          // Use the 12-month average data from API
          avgData = {} as any;
          metrics.forEach(metric => {
            (avgData as any)[metric.id] = average12MonthData[metric.id] || 0;
          });
          (avgData as any).month = item.month;
        } else if (comparisonSource !== 'average12') {
          const compareData = comparisonSource === 'all' ? allRestaurantsData : brandData;
          
          for (let i = 0; i < compareData.length; i++) {
            if (compareData[i]?.month === item.month) {
              avgData = compareData[i];
              break;
            }
          }
        }
        
        if (!avgData && comparisonSource !== 'average12') {
          const itemMonthName = typeof item.month === 'string' && item.month.includes('-') 
            ? item.month.split('-')[0] 
            : item.month;
          
          const compareData = comparisonSource === 'all' ? allRestaurantsData : brandData;
          
          for (let i = 0; i < compareData.length; i++) {
            if (!compareData[i]) continue;
            const compareMonth = compareData[i]?.month || '';
            const compareMonthName = typeof compareMonth === 'string' && compareMonth.includes('-') 
              ? compareMonth.split('-')[0] 
              : compareMonth;
              
            if (compareMonthName === itemMonthName) {
              avgData = compareData[i];
              break;
            }
          }
        }
        
        metrics.forEach(metric => {
          const value = (item as any)[metric.id] || 0;
          const avgValue = avgData && (avgData as any)[metric.id] ? (avgData as any)[metric.id] : 0;

          if (metric.type != "percent") {
            diffObj[`${metric.id}_value`] = value;
            diffObj[`${metric.id}_diff`] = value ? ((value - avgValue) /value  * 100).toFixed(2) : 0;
          } else {
            diffObj[`${metric.id}_value`] = value;
            diffObj[`${metric.id}_diff`] = value? (value - avgValue).toFixed(2) : 0;
          }
        });
      } else {
        metrics.forEach(metric => {
          diffObj[`${metric.id}_value`] = null;
          diffObj[`${metric.id}_diff`] = null;
        });
      }
      
      return diffObj;
    });
  }, [allMonths, dataByMonth, comparisonSource, allRestaurantsData, brandData, metrics]);

  // Use centralized heatmap color function
  const getHeatmapColor = (diff: number | null, metricType: string) => {
    const isInverse = metricType === "تعداد_روز_بدون_فروش";
    const diffValue = diff !== null ? parseFloat(diff.toString()) : null;
    return getHeatmapColorHelper(diffValue, metricType, isInverse);
  };

  // Format values based on metric type
  const formatValue = (value: number | null, metricType: string) => {
    if (value === null || value === undefined) return '-';
    
    switch (metricType) {
      case 'currency':
        return new Intl.NumberFormat('fa-IR').format(Math.round(value));
      case 'percent':
        return `${value.toFixed(1)}%`;
      case 'count':
        return value;
      default:
        return new Intl.NumberFormat('fa-IR').format(Math.round(value));
    }
  };

  const handleMonthClick = (month: string, actualMonth?: string) => {
    const monthToFind = actualMonth || month;
    
    if (monthToFind === selectedMonth) {
      setSelectedMonth(null);
      if (onMonthSelect) {
        onMonthSelect(monthToFind, null);
      }
    } else {
      setSelectedMonth(monthToFind);
      
      const monthInfo = dataByMonth.byMonth[monthToFind] || dataByMonth.byMonthName[monthToFind];
      
      if (monthInfo && onMonthSelect) {
        onMonthSelect(monthToFind, monthInfo.data);
      } else if (onMonthSelect) {
        const monthData = branchData.find(item => {
          if (!item || !item.month) return false;
          
          const itemMonth = item.month;
          const isItemMonthString = typeof itemMonth === 'string';
          const isMonthString = typeof monthToFind === 'string';
          
          const itemMonthName = isItemMonthString && itemMonth.includes('-') 
            ? itemMonth.split('-')[0] 
            : itemMonth;
            
          const selectedMonthName = isMonthString && monthToFind.includes('-') 
            ? monthToFind.split('-')[0] 
            : monthToFind;
          
          return (
            itemMonth === monthToFind ||
            itemMonthName === selectedMonthName ||
            (isMonthString && monthToFind.includes('-') && 
             isItemMonthString && itemMonth.includes(selectedMonthName)) ||
            (isMonthString && !monthToFind.includes('-') && 
             isItemMonthString && itemMonth.endsWith(monthToFind))
          );
        });
        
        onMonthSelect(monthToFind, monthData || null);
      }
    }
  };

  const getSelectedMetricName = () => {
    return metrics.find(m => m.id === selectedMetric)?.name || '';
  };

  if (isLoading && showLoading) {
    return (
      <div className="card-base card-padding animate-enter animate-delay-3">
        <div className="flex items-center justify-center h-64">
          <div className="text-text-secondary">در حال بارگذاری نقشه حرارتی…</div>
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="card-base card-padding animate-enter animate-delay-3">
        <div className="flex items-center justify-center h-64">
          <div className="text-status-error">بارگذاری داده‌ها ناموفق بود؛ لطفاً دوباره تلاش کنید.</div>
        </div>
      </div>
    );
  }

  return (
    <div className="card-base card-padding animate-enter animate-delay-3">
      <ChartInfoButton 
        onClick={() => openGuide(CHART_GUIDES.performanceHeatmap)}
        className="mb-4"
      />
      <div className="card-header">
        <h3 className="title-card">
          نقشه حرارتی {getSelectedMetricName()}
        </h3>
        <p className="text-text-secondary text-center">
          مقایسه با {comparisonSource === 'all' ? 'میانگین بازار' : comparisonSource === 'average12' ? 'میانگین ۱۲ ماه گذشته شعبه' : 'میانگین گروه برند'} | {getTimeframeFromContext} ماه گذشته
        </p>
      </div>
      
      {/* Performance Legend */}
      <div className="bg-bg-secondary/50 rounded-lg p-4 mb-6">
        <h3 className="text-lg font-semibold text-text-primary mb-3">راهنمای عملکرد:</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="flex items-center space-x-2 space-x-reverse">
            <div className="w-4 h-4 rounded border border-border-primary heatmap-excellent"></div>
            <span className="text-xs text-text-secondary">عالی (+۱۵%)</span>
          </div>
          <div className="flex items-center space-x-2 space-x-reverse">
            <div className="w-4 h-4 rounded border border-border-primary heatmap-good"></div>
            <span className="text-xs text-text-secondary">خوب (+۱۰%)</span>
          </div>
          <div className="flex items-center space-x-2 space-x-reverse">
            <div className="w-4 h-4 rounded border border-border-primary heatmap-suitable"></div>
            <span className="text-xs text-text-secondary">مناسب (+۵%)</span>
          </div>
          <div className="flex items-center space-x-2 space-x-reverse">
            <div className="w-4 h-4 rounded border border-border-primary heatmap-average"></div>
            <span className="text-xs text-text-secondary">متوسط (±۲%)</span>
          </div>
          <div className="flex items-center space-x-2 space-x-reverse">
            <div className="w-4 h-4 rounded border border-border-primary heatmap-poor"></div>
            <span className="text-xs text-text-secondary">ضعیف (-۵%)</span>
          </div>
          <div className="flex items-center space-x-2 space-x-reverse">
            <div className="w-4 h-4 rounded border border-border-primary heatmap-bad"></div>
            <span className="text-xs text-text-secondary">نامناسب (-۱۰%)</span>
          </div>
          <div className="flex items-center space-x-2 space-x-reverse">
            <div className="w-4 h-4 rounded border border-border-primary heatmap-critical"></div>
            <span className="text-xs text-text-secondary">بحرانی (-۱۵%)</span>
          </div>
          <div className="flex items-center space-x-2 space-x-reverse">
            <div className="w-4 h-4 rounded border border-border-primary heatmap-no-data"></div>
            <span className="text-xs text-text-secondary">بدون داده</span>
          </div>
        </div>
      </div>
    
      {/* Heatmap Grid */}
      <div className="grid grid-cols-4 md:grid-cols-4 lg:grid-cols-4 gap-2 md:gap-3">
        {/* Display of current timeframe */}
        <div className="text-sm font-medium p-2 mb-4 text-center bg-primary-100 rounded-lg col-span-full">
          نمایش {getTimeframeFromContext} ماه گذشته
        </div>
        
        {diffData.length === 0 ? (
          <div className="col-span-full flex flex-col items-center justify-center p-8 intent-warning-bg rounded-lg border border-warning">
            <div className="w-12 h-12 mb-4 text-status-warning">
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
            <h3 className="title-card text-status-warning">نقشه حرارتی در حال بارگذاری</h3>
            <p className="text-center text-status-warning mb-2">داده‌ها بارگذاری شده‌اند اما هنوز نقشه حرارتی آماده نشده است.</p>
            <button 
              onClick={() => window.location.reload()} 
              className="px-4 py-2 bg-status-warning text-text-inverse rounded-lg mt-2 hover:bg-status-warning/90 transition-colors"
            >
              بارگذاری مجدد
            </button>
          </div>
        ) : (
          diffData.map((item, index) => {
            if (!item || !item.month) return null;
            
            const diffValue = item[`${selectedMetric}_diff`] !== null ? parseFloat(item[`${selectedMetric}_diff`]) : null;
            const actualValue = item[`${selectedMetric}_value`];
            const hasData = diffValue !== null && actualValue !== null;
            const metricType = metrics.find(m => m.id === selectedMetric)?.type || '';
            
            return (
              <div 
                key={`${item.month}-${index}`}
                onClick={() => hasData ? handleMonthClick(item.month, item.actualMonth) : null}
                className={`
                  relative aspect-square flex flex-col items-center justify-center 
                  p-2 sm:p-3 md:p-4 rounded-xl border-2 border-transparent
                  transition-all ease-in-out
                  ${hasData ? 'cursor-pointer hover:scale-105 hover:shadow-lg hover:border-white/30' : 'cursor-not-allowed opacity-60'}
                  ${(selectedMonth && ((item.actualMonth && selectedMonth === item.actualMonth) || selectedMonth === item.month)) 
                    ? 'ring-4 ring-warning-400 ring-opacity-80 transform scale-105 shadow-xl z-10' 
                    : 'hover:ring-2 hover:ring-white/20'
                  }
                `}
                style={{ 
                  backgroundColor: hasData ? getHeatmapColor(diffValue, selectedMetric) : 'var(--heatmap-no-data)',
                  boxShadow: hasData ? '0 4px 12px rgba(0,0,0,0.15)' : '0 2px 4px rgba(0,0,0,0.1)',
                  opacity: hasData ? 1 : 0.6
                }}
              >
                {/* Month Label */}
                <div className="text-center mb-1">
                  <p className={`font-bold text-xs sm:text-sm md:text-base ${
                    hasData ? 'text-text-inverse drop-shadow-md' : 'text-text-primary'
                  }`}>
                    {formatMonthName(item.month)}
                  </p>
                  <p className={`font-bold text-xs sm:text-sm md:text-base ${
                    hasData ? 'text-text-inverse drop-shadow-md' : 'text-text-primary'
                  }`}>
                    {getYearFromMonth(item.month)}
                  </p>
                </div>

                {/* Data Content */}
                {hasData ? (
                  <div className="text-center flex-1 flex flex-col justify-center">
                    <p className="text-sm sm:text-base md:text-lg font-bold text-text-inverse drop-shadow-md leading-tight">
                      {formatValue(actualValue, metricType)}
                    </p>
                    <div className={`flex items-center justify-center mt-1 px-2 py-1 rounded-full text-xs font-medium ${
                    diffValue > 5 ? 'intent-success-bg text-theme-inverse' : 
                    diffValue < -5 ? 'intent-error-bg text-theme-inverse' : 
                    'surface-tertiary'
                  }`}>
                    <span className="mr-1">
                      {diffValue > 0 ? '↗' : diffValue < 0 ? '↘' : '→'}
                    </span>
                    <span>{Math.abs(diffValue)}%</span>
                  </div>
                  </div>
                ) : (
                  <div className="text-center flex-1 flex items-center justify-center">
                    <div className="text-text-primary">
                      <div className="w-8 h-8 mx-auto mb-2 opacity-40">
                        <svg fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M3 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <p className="text-xs">بدون داده</p>
                    </div>
                  </div>
                )}

                {/* Hover overlay */}
                {hasData && (
                  <div className="absolute inset-0 bg-bg-tertiary/10 opacity-0 hover:opacity-100 transition-opacity rounded-xl pointer-events-none" />
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default HeatMap;